import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, DollarSign, Percent, CreditCard, Package, 
  Truck, Plane, Loader2, Calendar, Download, BarChart3 
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { adminApi } from '../../lib/api';
import { formatPrice } from '../../lib/utils';
import { toast } from 'sonner';

const periods = [
  { key: 'week', label: 'Cette semaine' },
  { key: 'month', label: 'Ce mois' },
  { key: 'year', label: "Cette année" },
  { key: 'all', label: 'Tout' },
];

const AdminFinancialReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      try {
        const res = await adminApi.getFinancialReport(period);
        setData(res.data);
      } catch (err) {
        toast.error('Erreur lors du chargement du rapport');
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [period]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const maxRevenue = Math.max(...(data?.monthly_data?.map(m => m.revenue) || [1]));

  return (
    <div className="space-y-6" data-testid="financial-report-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-900 uppercase">
            Rapport financier
          </h1>
          <p className="text-slate-500 mt-1">Vue d'ensemble des finances de la plateforme</p>
        </div>
      </div>

      {/* Period Selector */}
      <div className="flex gap-2" data-testid="period-selector">
        {periods.map(p => (
          <Button
            key={p.key}
            variant={period === p.key ? "default" : "outline"}
            size="sm"
            onClick={() => setPeriod(p.key)}
            className={period === p.key ? 'bg-orange-500 hover:bg-orange-600' : ''}
            data-testid={`period-${p.key}`}
          >
            {p.label}
          </Button>
        ))}
      </div>

      {/* All-Time Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="key-metrics">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900" data-testid="total-revenue">
                  {formatPrice(data?.all_time?.total_revenue || 0)}
                </p>
                <p className="text-xs text-slate-500">Chiffre d'affaires total</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Percent className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900" data-testid="total-commission">
                  {formatPrice(data?.all_time?.total_commission || 0)}
                </p>
                <p className="text-xs text-slate-500">Commissions (15%)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900" data-testid="total-tva">
                  {formatPrice(data?.all_time?.total_tva || 0)}
                </p>
                <p className="text-xs text-slate-500">TVA collectée (20%)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900" data-testid="total-payouts">
                  {formatPrice(data?.all_time?.total_carrier_payouts || 0)}
                </p>
                <p className="text-xs text-slate-500">Versements cotransporteurs</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Period Stats */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-['Barlow_Condensed'] text-lg font-bold text-slate-900 uppercase flex items-center gap-2">
            <Calendar className="w-5 h-5 text-orange-500" />
            Statistiques de la période
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="p-3 bg-green-50 rounded-lg text-center">
              <p className="text-xl font-bold text-green-700" data-testid="period-revenue">
                {formatPrice(data?.period_stats?.revenue || 0)}
              </p>
              <p className="text-xs text-green-600">Revenus</p>
            </div>
            <div className="p-3 bg-orange-50 rounded-lg text-center">
              <p className="text-xl font-bold text-orange-700" data-testid="period-commission">
                {formatPrice(data?.period_stats?.commission || 0)}
              </p>
              <p className="text-xs text-orange-600">Commissions</p>
            </div>
            <div className="p-3 bg-red-50 rounded-lg text-center">
              <p className="text-xl font-bold text-red-700" data-testid="period-tva">
                {formatPrice(data?.period_stats?.tva || 0)}
              </p>
              <p className="text-xs text-red-600">TVA</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-xl font-bold text-blue-700" data-testid="period-payouts">
                {formatPrice(data?.period_stats?.payouts || 0)}
              </p>
              <p className="text-xs text-blue-600">Versements</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg text-center">
              <p className="text-xl font-bold text-slate-700" data-testid="period-parcels">
                {data?.period_stats?.parcel_count || 0}
              </p>
              <p className="text-xs text-slate-600">Colis</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Revenue Chart */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-['Barlow_Condensed'] text-lg font-bold text-slate-900 uppercase flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-orange-500" />
            Évolution des revenus (6 derniers mois)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-2 h-48" data-testid="revenue-chart">
            {data?.monthly_data?.map((month, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-xs font-medium text-slate-700">
                  {month.revenue > 0 ? formatPrice(month.revenue) : ''}
                </span>
                <div className="w-full flex flex-col gap-0.5" style={{ height: '140px' }}>
                  <div className="flex-1" />
                  <div
                    className="w-full bg-gradient-to-t from-orange-500 to-orange-400 rounded-t-md transition-all"
                    style={{ height: `${maxRevenue > 0 ? (month.revenue / maxRevenue) * 120 : 2}px`, minHeight: '2px' }}
                    title={`${month.month}: ${formatPrice(month.revenue)}`}
                  />
                  <div
                    className="w-full bg-gradient-to-t from-blue-400 to-blue-300 rounded-t-sm"
                    style={{ height: `${maxRevenue > 0 ? (month.commission / maxRevenue) * 120 : 1}px`, minHeight: '1px' }}
                    title={`Commission: ${formatPrice(month.commission)}`}
                  />
                </div>
                <span className="text-xs text-slate-500">{month.month}</span>
                <span className="text-xs text-slate-400">{month.parcel_count} colis</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-6 mt-4 justify-center">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-orange-500 rounded-sm" />
              <span className="text-xs text-slate-600">Revenus</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-400 rounded-sm" />
              <span className="text-xs text-slate-600">Commissions</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Status Breakdown */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-['Barlow_Condensed'] text-lg font-bold text-slate-900 uppercase">
              Répartition des colis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3" data-testid="status-breakdown">
              {Object.entries(data?.all_time?.status_breakdown || {}).map(([status, count]) => {
                const statusLabels = {
                  created: 'Créés', paid: 'Payés', accepted: 'Acceptés',
                  in_progress: 'En cours', delivered: 'Livrés', cancelled: 'Annulés',
                };
                const statusColors = {
                  created: 'bg-slate-200', paid: 'bg-yellow-200', accepted: 'bg-blue-200',
                  in_progress: 'bg-orange-200', delivered: 'bg-green-200', cancelled: 'bg-red-200',
                };
                const total = data?.all_time?.total_parcels || 1;
                const pct = Math.round((count / total) * 100);
                return (
                  <div key={status} className="flex items-center gap-3">
                    <span className="text-sm text-slate-700 w-24">{statusLabels[status] || status}</span>
                    <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${statusColors[status] || 'bg-slate-300'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-slate-900 w-16 text-right">{count} ({pct}%)</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Transport Modes & Pending Payouts */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-['Barlow_Condensed'] text-lg font-bold text-slate-900 uppercase">
              Modes de transport & Versements
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <p className="text-sm font-medium text-slate-600 mb-3">Modes de transport des trajets</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-orange-50 rounded-lg flex items-center gap-3" data-testid="road-trips-count">
                  <Truck className="w-6 h-6 text-orange-600" />
                  <div>
                    <p className="text-xl font-bold text-slate-900">{data?.transport_modes?.road || 0}</p>
                    <p className="text-xs text-slate-500">Routier</p>
                  </div>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg flex items-center gap-3" data-testid="air-trips-count">
                  <Plane className="w-6 h-6 text-blue-600" />
                  <div>
                    <p className="text-xl font-bold text-slate-900">{data?.transport_modes?.air || 0}</p>
                    <p className="text-xs text-slate-500">Fret aérien</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-600 mb-3">Versements en attente</p>
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-2xl font-bold text-yellow-700" data-testid="pending-payouts">
                  {formatPrice(data?.all_time?.pending_carrier_payouts || 0)}
                </p>
                <p className="text-sm text-yellow-600">à verser aux cotransporteurs</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminFinancialReport;
